import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, Plane, Wallet, Crown, LogOut, ShieldCheck, Bell, Compass, Download, Loader2, Database, AlertCircle, CheckCircle2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { useTheme } from '@/lib/theme';
import { useAuth } from '@/lib/auth';
import { Cards as CardsApi, Transactions as TxnsApi } from '@/lib/api';
import { toast } from 'sonner';

const GOALS = [
  { id: 'cashback', icon: Wallet, title: 'Maximize cashback', desc: 'Optimize for instant rupee value back.' },
  { id: 'travel', icon: Plane, title: 'Travel rewards', desc: 'Stack miles, lounges, and tier benefits.' },
  { id: 'milestone', icon: Crown, title: 'Milestones & luxury', desc: 'Hit annual milestones, lounges, concierge, hotel perks.' },
];

function initialsFromName(name) {
  if (!name) return 'U';
  return name
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export default function Settings() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { user, patchMe, logout, migrateDemo, loading: authLoading } = useAuth();

  const [name, setName] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingGoal, setSavingGoal] = useState(null); // goalId being saved

  // Import-demo state — used by the new "Starter data" card so a user who
  // skipped the post-Google-login prompt can still import the curated demo
  // portfolio. We probe `/cards/mine` to decide whether the import is
  // currently allowed (the backend refuses if user already has cards).
  const [hasCards, setHasCards] = useState(null); // null = unknown
  const [hasTxns, setHasTxns] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateConfirmOpen, setMigrateConfirmOpen] = useState(false);

  useEffect(() => {
    if (user?.name) setName(user.name);
  }, [user]);

  // Light fingerprint of the user's portfolio — refreshed every time the
  // user reaches /settings. Cheap enough to do on every visit.
  useEffect(() => {
    let cancelled = false;
    if (!user?.id) return;
    (async () => {
      try {
        const [cards, summary] = await Promise.all([
          CardsApi.mine().catch(() => []),
          TxnsApi.summary().catch(() => null),
        ]);
        if (cancelled) return;
        setHasCards((cards || []).length > 0);
        setHasTxns((summary?.total?.count || 0) > 0);
      } catch (_) {
        if (!cancelled) { setHasCards(false); setHasTxns(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const onSaveProfile = async () => {
    if (!name || name.trim() === user?.name) {
      toast.message('Nothing to save.');
      return;
    }
    setSavingProfile(true);
    try {
      await patchMe({ name: name.trim() });
      toast.success('Profile updated');
    } catch (e) {
      toast.error(e?.message || 'Could not save profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const onChooseGoal = async (goalId) => {
    if (goalId === user?.goal) return;
    setSavingGoal(goalId);
    try {
      await patchMe({ goal: goalId });
      toast.success(`Goal set to ${GOALS.find((g) => g.id === goalId)?.title || goalId}`);
    } catch (e) {
      toast.error(e?.message || 'Could not save goal');
    } finally {
      setSavingGoal(null);
    }
  };

  const onSignOut = () => {
    logout();
    toast.message('Signed out');
    navigate('/');
  };

  const onImportDemo = async () => {
    setMigrating(true);
    try {
      const r = await migrateDemo();
      toast.success(`Imported ${r.cards_copied} card${r.cards_copied === 1 ? '' : 's'} · ${r.transactions_copied} txn${r.transactions_copied === 1 ? '' : 's'} · ${r.leakage_copied} leakage event${r.leakage_copied === 1 ? '' : 's'}`);
      setMigrateConfirmOpen(false);
      setHasCards(true);
      navigate('/cards');
    } catch (e) {
      toast.error(e?.message || 'Could not import demo data');
    } finally {
      setMigrating(false);
    }
  };

  const isDemoUser = user?.email === 'aarav.mehta@example.com';
  const canImport = !isDemoUser && hasCards === false && hasTxns === false;

  // Guard against rendering the form with a null user (causes a brief
  // "Guest" flash + empty inputs while the auth context is still hydrating).
  if (authLoading || !user) {
    return (
      <div className="min-h-[40vh] grid place-items-center text-muted-foreground" data-testid="settings-loading">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const initials = initialsFromName(user?.name);

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <div className="text-xs uppercase tracking-[0.18em] text-primary font-medium">Account</div>
        <h1 className="mt-2 font-display text-2xl sm:text-3xl font-semibold tracking-tight">Settings</h1>
        <p className="mt-1 text-sm text-muted-foreground">Tune your profile, goals, and preferences.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Profile */}
        <Card className="p-6 lg:col-span-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-primary/10 border border-primary/20 grid place-items-center text-primary font-semibold" data-testid="settings-avatar">
              {initials}
            </div>
            <div>
              <div className="font-display text-base font-semibold" data-testid="settings-display-name">{user?.name || '—'}</div>
              <div className="text-xs text-muted-foreground" data-testid="settings-email">{user?.email || '—'}</div>
            </div>
          </div>
          <div className="mt-4 space-y-3 text-sm">
            <div>
              <Label className="text-xs">Display name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-10 mt-1"
                placeholder="Your name"
                data-testid="settings-name-input"
              />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input value={user?.email || ''} className="h-10 mt-1" readOnly />
              <p className="mt-1 text-[10px] text-muted-foreground">Email changes require a re-verification flow (coming with Google sign-in).</p>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <Button size="sm" onClick={onSaveProfile} disabled={savingProfile} className="gap-1.5" data-testid="settings-save-profile">
              {savingProfile && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Save profile
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 text-rose-500 hover:text-rose-500"
              onClick={onSignOut}
              data-testid="settings-sign-out"
            >
              <LogOut className="h-4 w-4" /> Sign out
            </Button>
          </div>
        </Card>

        {/* Goals */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <h3 className="font-display text-base font-semibold">Reward goal</h3>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">We tune recommendations and milestone hints based on your primary goal.</p>

          <div className="mt-4 grid sm:grid-cols-3 gap-3" data-testid="settings-goal-select">
            {GOALS.map((g) => {
              const Icon = g.icon;
              const selected = user?.goal === g.id;
              const saving = savingGoal === g.id;
              return (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => onChooseGoal(g.id)}
                  disabled={!!savingGoal}
                  data-testid={`settings-goal-${g.id}`}
                  className={`text-left rounded-xl border p-4 transition-colors disabled:cursor-not-allowed ${selected ? 'border-primary/40 bg-primary/5' : 'border-border bg-[hsl(var(--surface-2))] hover:bg-[hsl(var(--surface-3))]'}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 border border-primary/20 grid place-items-center">
                      <Icon className="h-4 w-4 text-primary" />
                    </div>
                    {selected && <Badge className="bg-primary/15 text-primary border-primary/30">Selected</Badge>}
                    {saving && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
                  </div>
                  <div className="mt-3 font-display text-sm font-semibold">{g.title}</div>
                  <div className="mt-1 text-xs text-muted-foreground">{g.desc}</div>
                </button>
              );
            })}
          </div>
        </Card>
      </div>

      {/* Preferences */}
      <Card className="p-6">
        <h3 className="font-display text-base font-semibold">Preferences</h3>
        <div className="mt-4 divide-y divide-border">
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[hsl(var(--surface-2))] grid place-items-center">
                <Sparkles className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium">Theme</div>
                <div className="text-xs text-muted-foreground">Currently {theme}</div>
              </div>
            </div>
            <ThemeToggle />
          </div>
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[hsl(var(--surface-2))] grid place-items-center">
                <Bell className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium">Missed reward alerts</div>
                <div className="text-xs text-muted-foreground">Get pinged when you skip an optimal card.</div>
              </div>
            </div>
            <Switch defaultChecked data-testid="settings-toggle-alerts" />
          </div>
          <div className="py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-[hsl(var(--surface-2))] grid place-items-center">
                <ShieldCheck className="h-4 w-4 text-primary" />
              </div>
              <div>
                <div className="text-sm font-medium">Privacy mode</div>
                <div className="text-xs text-muted-foreground">Mask amounts in screenshots and screen shares.</div>
              </div>
            </div>
            <Switch />
          </div>
        </div>
      </Card>

      {/* Starter data — import the curated demo portfolio so new accounts
          aren't empty on day one. Hidden for the demo user itself. */}
      {!isDemoUser && (
        <Card className="p-6" data-testid="settings-import-demo-card">
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 rounded-lg bg-[hsl(var(--surface-2))] grid place-items-center shrink-0">
              <Database className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-display text-base font-semibold">Starter data</h3>
                {canImport && <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-600 bg-amber-500/5">Available</Badge>}
                {!canImport && hasCards != null && <Badge variant="outline" className="text-[10px] border-emerald-500/30 text-emerald-600 bg-emerald-500/5">Already populated</Badge>}
              </div>
              <p className="mt-1 text-xs text-muted-foreground leading-relaxed max-w-xl">
                Import the curated demo portfolio &mdash; 6 cards across HSBC, Axis, HDFC, ICICI, IndusInd &amp; SBI plus
                sample transactions and leakage events. Lets you test the recommender immediately without onboarding cards manually.
              </p>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                {canImport ? (
                  <Button
                    size="sm"
                    onClick={() => setMigrateConfirmOpen(true)}
                    disabled={migrating}
                    className="gap-1.5"
                    data-testid="settings-import-demo-button"
                  >
                    {migrating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    <Database className="h-3.5 w-3.5" /> Import demo portfolio
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled
                    className="gap-1.5"
                    title="You already have cards or transactions in your account."
                  >
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> Already populated
                  </Button>
                )}
                {!canImport && hasCards && (
                  <span className="text-[11px] text-muted-foreground inline-flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" /> Clear your portfolio in <b>My Cards</b> first to re-import.
                  </span>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Chrome extension */}
      <Card className="p-6" data-testid="settings-extension-card">
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 rounded-lg bg-[hsl(var(--surface-2))] grid place-items-center shrink-0">
            <Compass className="h-5 w-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-display text-base font-semibold">Chrome extension</h3>
              <Badge variant="outline" className="text-[10px]">Beta · MV3</Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground leading-relaxed max-w-xl">
              Get card-aware suggestions on <b>any website</b> &mdash; the extension watches for a
              cart total (₹/Rs/INR) on the page and shows your top 3 cards inline. For supported
              merchants (Amazon, Flipkart, Swiggy, Zomato, Myntra, MakeMyTrip, BookMyShow, Tata Neu,
              Croma, &amp; growing) it also surfaces smart voucher/portal routes (e.g. &ldquo;buy an Amazon voucher
              via HDFC SmartBuy&rdquo;). Install via Chrome&apos;s
              <span className="font-medium"> Developer mode → Load unpacked</span> after extracting the ZIP.
            </p>
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL || ''}/api/extension/download`}
                target="_blank"
                rel="noopener"
                data-testid="settings-extension-download"
              >
                <Button size="sm" className="gap-1.5">
                  <Download className="h-3.5 w-3.5" /> Download extension (.zip)
                </Button>
              </a>
              <span className="text-[11px] text-muted-foreground">
                Works on every website with a detectable cart total.
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Import-demo confirm dialog */}
      <AlertDialog open={migrateConfirmOpen} onOpenChange={(v) => !migrating && setMigrateConfirmOpen(v)}>
        <AlertDialogContent data-testid="settings-import-demo-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display flex items-center gap-2">
              <Database className="h-4 w-4 text-primary" /> Import demo portfolio?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs leading-relaxed space-y-2">
              <span className="block">This copies the demo user&apos;s 6 cards (HSBC TravelOne, Axis Atlas, HDFC BizBlack, ICICI Sapphiro, IndusInd Qatar Avios, SBI Aurum) plus sample transactions and leakage events into <b>{user?.email}</b>.</span>
              <span className="block text-muted-foreground">Cards, transactions, and milestone progress are <b>duplicated</b> — the demo account is untouched. You can clear and reimport from My Cards anytime.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={migrating} data-testid="settings-import-demo-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onImportDemo} disabled={migrating} data-testid="settings-import-demo-confirm">
              {migrating ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : null}
              Import demo
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
